import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useAuth } from "../App";
import { toast } from "sonner";
import { api } from "../services/apiClient";
import { useFormDraft } from "../hooks/useFormDraft";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

const createInitialSettings = () => ({
  tutorial_video_url: "",
  email_subject: "",
  email_body: "",
  welcome_email_enabled: true,
});

const AdminSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(createInitialSettings());
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [loading, setLoading] = useState(false);
  const draftKey = useMemo(
    () => `draft:admin-settings:${user?.id || user?.email || "anon"}`,
    [user],
  );
  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    return JSON.stringify(settings) !== initialSnapshot;
  }, [settings, initialSnapshot]);
  const { clearDraft } = useFormDraft({
    draftKey,
    formData: settings,
    setFormData: setSettings,
    enabled: Boolean(user),
  });
  const { confirmIfDirty } = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get("/admin/settings");
      const data = await response.json();
      const merged = { ...createInitialSettings(), ...data };
      setSettings(merged);
      setInitialSnapshot(JSON.stringify(merged));
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put("/admin/settings", settings);
      toast.success("Configuración guardada");
      clearDraft();
      setInitialSnapshot(JSON.stringify(settings));
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-4">
          <BackNavigationButton
            dataTestId="admin-settings-back"
            onBeforeNavigate={confirmIfDirty}
          />
        </div>
        <h1 className="text-3xl font-bold mb-8">Configuración de Onboarding</h1>

        <form
          onSubmit={handleSubmit}
          className="bg-background rounded-lg p-6 space-y-6"
        >
          <div>
            <label className="text-sm font-medium mb-2 block">
              URL Video Tutorial (YouTube)
            </label>
            <Input
              value={settings.tutorial_video_url}
              onChange={(e) =>
                setSettings({ ...settings, tutorial_video_url: e.target.value })
              }
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Asunto del Email
            </label>
            <Input
              value={settings.email_subject}
              onChange={(e) =>
                setSettings({ ...settings, email_subject: e.target.value })
              }
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Cuerpo del Email (HTML)
            </label>
            <Textarea
              value={settings.email_body}
              onChange={(e) =>
                setSettings({ ...settings, email_body: e.target.value })
              }
              rows={10}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.welcome_email_enabled}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  welcome_email_enabled: e.target.checked,
                })
              }
            />
            <label className="text-sm">
              Enviar email de bienvenida automáticamente
            </label>
          </div>

          <Button type="submit" disabled={loading}>
            {loading ? "Guardando..." : "Guardar cambios"}
          </Button>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default AdminSettings;
